import * as Sentry from '@sentry/react';
import {
  createConversation,
  regenerateConversationMessage,
  updateConversation,
} from 'api/v2/AI';
import { useAiMetadata } from 'contexts/AIMetadataProvider';
import useQueryClient from 'hooks/react-query/useClientQuery';
import useFeatureLock from 'hooks/Subscriptions/useFeatureLock';
import type { FC } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import ReactSpeechRecognition from 'react-speech-recognition';
import type {
  ConversationMessagePart,
  ConversationMetadata,
  ConversationQuery,
  Message,
  StreamingChunk,
} from 'types/ai.types';
import {
  AIAgentEnum,
  AIRoleEnum,
  type AIUsage,
  type ConversationHistory,
  type ConversationMessage,
} from 'types/ai.types';
import { AICacheKeys, UserCacheKeys } from 'types/cacheKeys.types';
import {
  SubscriptionCounterKeysEnum,
  SubscriptionLockKeysEnum,
} from 'types/subscriptions.types';
import { logAmplitude } from 'utils/amplitude';
import {
  WEB_AIASSISTANT_NEWCHAT,
  WEB_AIASSISTANT_USERSENTMESSAGE,
} from 'utils/amplitude/events/ai.amplitude';
import { SentryIgnoredError } from 'utils/SentryIgnoredError';
import { ONE_SECOND } from 'utils/time';
import showToast from 'utils/toast';
import {
  aiAdvisorMonthlyLimitLockContent,
  aiAdvisorPageLock,
} from '../PaywallLockV2/locks.content';
import type { AIChatContextValue, AIChatProviderProps } from './AIChat.types';
import {
  useAIChatAttachment,
  useConversationMetadata,
  useConversationQuery,
  useGetGratificationById,
  useUsageQuery,
} from './hooks';
import { scrollToBottom } from './utils';

export const AIChatContext = createContext<AIChatContextValue>(
  {} as AIChatContextValue,
);

export const AIChatProvider: FC<AIChatProviderProps> = ({
  children,
  aiAgent = AIAgentEnum.default,
  allowFileMetadata = false,
  ignoreLocking = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoPrompt, setIsAutoPrompt] = useState(false);
  const [message, setMessage] = useState('');
  const [currentTipId, setCurrentTipId] = useState('');
  const [streamedData, setStreamedData] = useState<ConversationMessagePart[]>(
    [],
  );
  const [conversationId, setConversationId] = useState('');
  const [conversation, setConversation] = useState<ConversationHistory | null>(
    null,
  );

  const { metadata, setMetadata } = useAiMetadata();

  const location = useLocation();

  const { data: gratification } = useGetGratificationById(currentTipId);
  const abortController = useRef(new AbortController());
  const processedChunksCountRef = useRef<number>(0);

  const { refetch: refetchUsage, data: usage = {} as AIUsage } = useUsageQuery(
    conversation,
    message,
  );

  const { metadataItems, refetchMetadata } = useConversationMetadata(
    conversationId,
    metadata,
  );

  const { t } = useTranslation();

  const isStreaming = useMemo(() => !!streamedData.length, [streamedData]);

  const allMessages: Message[] = useMemo(() => {
    const messages = conversation?.messages || [];
    const streamedMessage = streamedData.length
      ? {
          _id: `streaming_message`,
          role: AIRoleEnum.assistant,
          parts: streamedData,
          content: '',
        }
      : null;

    return streamedMessage ? [...messages, streamedMessage] : messages;
  }, [conversation?.messages, streamedData]);

  const metadataItemsMap = useMemo(() => {
    if (!metadataItems) return {};

    return metadataItems.reduce((acc, item) => {
      if (item._id) acc[item._id] = item;
      return acc;
    }, {} as Record<string, ConversationMetadata>);
  }, [metadataItems]);

  const queryClient = useQueryClient();
  const attachmentApi = useAIChatAttachment(allowFileMetadata);

  const { refetch: refetchConversation } = useConversationQuery(
    conversationId,
    (data) => {
      scrollToBottom();
      setConversation(data);
      setConversationId(data?._id || '');
      setStreamedData([]);

      attachmentApi.remove();
      if (conversationId) {
        refetchUsage();
      }
    },
  );

  useEffect(() => {
    setCurrentTipId('');
    setConversation(null);
  }, [location.pathname]);

  const onDownloadProgress = (progress: ProgressEvent): void => {
    const xhr = progress?.target as XMLHttpRequest | null;
    const buffer = (xhr?.response as string | undefined) || '';
    const chunks = buffer.split('\n') as string[];
    chunks.pop();

    const unhandled_chunks = chunks
      .slice(processedChunksCountRef.current)
      .filter((chunk) => chunk !== '' && !!chunk);
    if (unhandled_chunks.length === 0) return;

    for (const line of unhandled_chunks) {
      const chunk: StreamingChunk = JSON.parse(line);
      processedChunksCountRef.current++;

      if (
        chunk?.finish_reason ||
        (chunk?.parts?.length === 1 && chunk?.parts[0]?.type === 'consent_text')
      ) {
        if (conversationId !== chunk?.conversationId) {
          setConversationId(chunk?.conversationId || '');
        } else {
          refetchConversation();
        }

        if (chunk?.finish_reason === 'error') {
          showToast(
            { title: t('backend.errors.unexpected_error') },
            { type: 'error' },
          );
        }

        continue;
      }

      setStreamedData((storedParts) => {
        const incomingParts = chunk?.parts || [];
        if (incomingParts.length === 0) return storedParts;

        const currentParts = [...storedParts];
        const lastStoredPart = currentParts[currentParts.length - 1];
        const newParts = [...currentParts];

        for (let i = 0; i < incomingParts.length; i++) {
          const incomingPart = incomingParts[i];
          const isFirstIncomingPart = i === 0;

          const canConcatenate =
            isFirstIncomingPart &&
            lastStoredPart &&
            lastStoredPart.type === incomingPart.type &&
            (incomingPart.type === 'text' ||
              incomingPart.type === 'thinking') &&
            'content' in lastStoredPart &&
            'content' in incomingPart;

          if (canConcatenate) {
            newParts[newParts.length - 1] = {
              ...lastStoredPart,
              content: lastStoredPart.content + incomingPart.content,
            };
          } else {
            newParts.push(incomingPart);
          }
        }

        return newParts;
      });
    }
  };

  const parseMetadata = () => {
    if (metadata) return metadata;
    if (currentTipId && gratification) {
      return {
        documentId: currentTipId,
        documentType: 'gratifications',
        content: gratification?.description || gratification?.title,
      };
    }
    return undefined;
  };

  const mutateConversation = (
    conversationMessage: Omit<ConversationMessage, '_id'>,
    id?: string,
  ) => {
    setStreamedData([]);
    processedChunksCountRef.current = 0;
    logAmplitude(WEB_AIASSISTANT_USERSENTMESSAGE);
    ReactSpeechRecognition?.stopListening?.();
    abortController.current = new AbortController();
    setIsLoading(true);
    setMessage('');

    (id ? updateConversation : createConversation)(
      {
        id,
        message: conversationMessage,
        ai_agent: aiAgent !== AIAgentEnum.default ? aiAgent : undefined,
        metadata: parseMetadata(),
      } as ConversationQuery,
      {
        onDownloadProgress,
        responseType: 'stream',
        signal: abortController.current.signal,
        timeout: 180 * ONE_SECOND,
      },
    )
      .then(() => {
        if (!id) {
          queryClient?.invalidateQueries(AICacheKeys.ConversationsList);
          queryClient?.invalidateQueries(UserCacheKeys.FeatureCounts);
        } else {
          refetchConversation();
          refetchMetadata();
        }
        setIsAutoPrompt(false);
        scrollToBottom();

        if (
          attachmentApi.attachment?.type === 'pdf' ||
          attachmentApi.attachment?.type === 'image'
        ) {
          attachmentApi.remove();
          setMetadata(null, attachmentApi.attachment?.type);
        }
      })
      .catch((error) => {
        showToast(
          { title: t('backend.errors.unexpected_error') },
          { type: 'error' },
        );
        Sentry.captureException(error);
      })
      .finally(() => setIsLoading(false));
  };

  const disabledSend =
    isLoading || usage?.reachedMaxCount || usage?.messageLengthExceeded;

  const { withPaywallLock, withPaywallCounterLock } = useFeatureLock();

  const handleSend = (suggestedMessage?: string) => {
    const action = () => {
      if (disabledSend) return;

      const conversationMessage: Omit<ConversationMessage, '_id'> = {
        role: AIRoleEnum.user,
        parts: [
          {
            type: 'text',
            content: suggestedMessage || message,
          },
        ],
        content: suggestedMessage || message,
      };

      if (
        attachmentApi.attachment &&
        (metadata?.documentType === 'image' || metadata?.documentType === 'pdf')
      ) {
        conversationMessage.metadataId = metadata?._id;
      }

      const newConversation = suggestedMessage
        ? {
            messages: [{ ...conversationMessage, _id: 'tempId' }],
            status: 'processing',
            user_message_count: 1,
          }
        : {
            ...(conversation || {}),
            messages: [
              ...(conversation?.messages || []),
              { ...conversationMessage, _id: 'tempId' },
            ],
            status: 'processing',
            user_message_count: 1,
          };

      setConversation(newConversation as ConversationHistory);
      attachmentApi.remove();

      scrollToBottom();

      return mutateConversation(
        conversationMessage,
        suggestedMessage ? undefined : conversation?._id,
      );
    };

    if (ignoreLocking) {
      return withPaywallCounterLock(
        action,
        SubscriptionCounterKeysEnum.taxAdvisorMonthlyMessages,
        aiAdvisorMonthlyLimitLockContent,
      );
    }

    return withPaywallLock(
      () =>
        withPaywallCounterLock(
          action,
          SubscriptionCounterKeysEnum.taxAdvisorMonthlyMessages,
          aiAdvisorMonthlyLimitLockContent,
        ),
      SubscriptionLockKeysEnum.viewWebAIAdvisorPage,
      aiAdvisorPageLock as any,
    );
  };

  const handleRegenerateMessage = (
    messageId: string,
    parts?: ConversationMessagePart[],
  ) => {
    if (!conversation?._id) return;

    // For regeneration, we need to preserve existing parts and remove the message from conversation
    if (conversation && conversation.messages.length > 0) {
      const lastMessage =
        conversation.messages[conversation.messages.length - 1];

      // Initialize streaming with the existing parts from the message being regenerated
      setStreamedData(parts || lastMessage.parts || []);

      // Remove the last message from conversation (without mutating)
      const updatedMessages = conversation.messages.slice(0, -1);
      setConversation({
        ...conversation,
        messages: updatedMessages,
      });
    } else {
      setStreamedData([]);
    }

    abortController.current = new AbortController();
    processedChunksCountRef.current = 0;
    setIsLoading(true);

    return regenerateConversationMessage(conversation._id, messageId, {
      onDownloadProgress,
      responseType: 'stream',
      signal: abortController.current.signal,
      timeout: 180 * ONE_SECOND,
    })
      .catch((error) => {
        showToast(
          { title: t('backend.errors.unexpected_error') },
          { type: 'error' },
        );
        Sentry.captureException(error);
      })
      .finally(() => setIsLoading(false));
  };

  const handleNewChat = () => {
    setCurrentTipId('');
    abortController.current.abort();
    setConversation(null);
    setConversationId('');
    setStreamedData([]);
    document.getElementById('ai-assistant-textarea')?.focus();
    logAmplitude(WEB_AIASSISTANT_NEWCHAT);
    attachmentApi.remove();
  };

  return (
    <AIChatContext.Provider
      value={{
        aiAgent,
        conversation,
        conversationId,
        currentTipId,
        isAutoPrompt,
        isLoading,
        message,
        isStreaming,
        allMessages,
        usage,
        attachmentApi,
        metadataItemsMap,
        handleNewChat,
        handleSend,
        handleRegenerateMessage,
        refetchConversation,
        setConversation,
        setConversationId,
        setCurrentTipId,
        setIsAutoPrompt,
        setMessage,
      }}
    >
      {children}
      {attachmentApi.enabled && (
        <input {...attachmentApi.dropzoneProps.input()} />
      )}
    </AIChatContext.Provider>
  );
};

export const useAIChatContext = (): AIChatContextValue => {
  const context = useContext(AIChatContext);
  if (!context) {
    throw new SentryIgnoredError(
      'useAIChatContext must be used within a AIChatProvider',
    );
  }
  return context;
};
